import type { FlightDto, StatsDto, UserDto } from '@flightlog/shared';
import { lazy, Suspense, useCallback, useEffect, useState } from 'react';
import { deleteFlight, ensureAuth, getFlights, getStats, logout } from './api';
import { AddFlight } from './components/AddFlight';
import { FlightDetail } from './components/FlightDetail';
import { FlightsView } from './components/FlightsView';
import { SettingsView } from './components/SettingsView';
import { StatsView } from './components/StatsView';
import { formatDistance, plural } from './format';
import { ChartIcon, GearIcon, GlobeIcon, PlaneIcon, PlusIcon } from './icons';
import { haptic, setBackButton } from './telegram';

const MapView = lazy(() => import('./components/MapView'));

type Tab = 'flights' | 'map' | 'stats' | 'settings';

const TITLES: Record<Tab, string> = {
  flights: 'Мои полёты',
  map: 'Карта',
  stats: 'Статистика',
  settings: 'Настройки',
};

function initialTab(): Tab {
  const requested = new URLSearchParams(window.location.search).get('tab');
  return requested === 'map' || requested === 'stats' || requested === 'settings'
    ? requested
    : 'flights';
}

export default function App() {
  const [tab, setTab] = useState<Tab>(initialTab);
  const [user, setUser] = useState<UserDto | null>(null);
  const [flights, setFlights] = useState<FlightDto[]>([]);
  const [stats, setStats] = useState<StatsDto | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [selected, setSelected] = useState<FlightDto | null>(null);
  const [addOpen, setAddOpen] = useState(false);

  const load = useCallback(async () => {
    const [nextFlights, nextStats] = await Promise.all([getFlights(), getStats()]);
    setFlights(nextFlights);
    setStats(nextStats);
  }, []);

  const boot = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const account = await ensureAuth();
      setUser(account);
      await load();
    } catch {
      setError('Не удалось загрузить данные. Проверь, что API запущен.');
    } finally {
      setLoading(false);
    }
  }, [load]);

  useEffect(() => {
    void boot();
  }, [boot]);

  useEffect(() => {
    if (selected) {
      setBackButton(true, () => setSelected(null));
    } else if (addOpen) {
      setBackButton(true, () => setAddOpen(false));
    } else {
      setBackButton(false);
    }
    return () => setBackButton(false);
  }, [selected, addOpen]);

  async function handleDelete(id: string) {
    await deleteFlight(id);
    setSelected(null);
    await load();
  }

  async function handleCreated() {
    setAddOpen(false);
    await load();
  }

  async function handleLogout() {
    await logout();
    window.location.reload();
  }

  const subtitle = stats
    ? `${stats.totalFlights} ${plural(stats.totalFlights, ['рейс', 'рейса', 'рейсов'])} · ${formatDistance(stats.totalDistanceKm)}`
    : '';

  return (
    <div className="app">
      <header className="header">
        <div className="header-row">
          <span className="brand">
            <PlaneIcon size={16} />
            Jetlog
          </span>
          {user?.firstName ? <span className="header-hello">привет, {user.firstName}</span> : null}
        </div>
        <h1>{TITLES[tab]}</h1>
        {/* В «Моих полётах» те же цифры уже на табло. */}
        {subtitle && tab !== 'flights' ? <p className="sub">{subtitle}</p> : null}
      </header>

      <main className="main">
        {loading ? (
          <div className="skeleton-list">
            {[0, 1, 2, 3].map((item) => (
              <div className="skeleton" key={item} />
            ))}
          </div>
        ) : error ? (
          <div className="empty">
            <h2>Что-то не так</h2>
            <p>{error}</p>
            <button type="button" className="button button-primary" onClick={() => void boot()}>
              Повторить
            </button>
          </div>
        ) : tab === 'flights' ? (
          <FlightsView
            flights={flights}
            stats={stats}
            onSelect={setSelected}
            onAdd={() => setAddOpen(true)}
          />
        ) : tab === 'map' ? (
          <Suspense fallback={<div className="map-wrap map-loading">Загружаю карту…</div>}>
            <MapView flights={flights} onSelectFlight={setSelected} />
          </Suspense>
        ) : tab === 'stats' && stats ? (
          <StatsView stats={stats} flights={flights} onSelectFlight={setSelected} />
        ) : tab === 'settings' && user ? (
          <SettingsView user={user} flightsCount={flights.length} onLogout={() => void handleLogout()} />
        ) : null}
      </main>

      {tab === 'flights' && !loading && !error ? (
        <button
          type="button"
          className="fab"
          aria-label="Добавить рейс"
          onClick={() => {
            haptic('medium');
            setAddOpen(true);
          }}
        >
          <PlusIcon />
        </button>
      ) : null}

      <nav className="tabbar">
        <button
          type="button"
          className={`tab ${tab === 'flights' ? 'tab-active' : ''}`}
          onClick={() => setTab('flights')}
        >
          <PlaneIcon size={21} />
          <span>Полёты</span>
        </button>
        <button
          type="button"
          className={`tab ${tab === 'map' ? 'tab-active' : ''}`}
          onClick={() => setTab('map')}
        >
          <GlobeIcon size={21} />
          <span>Карта</span>
        </button>
        <button
          type="button"
          className={`tab ${tab === 'stats' ? 'tab-active' : ''}`}
          onClick={() => setTab('stats')}
        >
          <ChartIcon size={21} />
          <span>Статистика</span>
        </button>
        <button
          type="button"
          className={`tab ${tab === 'settings' ? 'tab-active' : ''}`}
          onClick={() => setTab('settings')}
        >
          <GearIcon size={21} />
          <span>Настройки</span>
        </button>
      </nav>

      {selected ? (
        <FlightDetail
          flight={selected}
          onClose={() => setSelected(null)}
          onDelete={handleDelete}
        />
      ) : null}

      {addOpen ? (
        <AddFlight onClose={() => setAddOpen(false)} onCreated={() => void handleCreated()} />
      ) : null}
    </div>
  );
}
